import { GuestGroup, guestGroups } from "../shared/guest-groups";
import { bindGuestGroupPicker, renderGuestGroupPicker, renderGroupTag } from "./guest-group-picker";
import { renderGuestCard, downloadGuestCard } from "./guest-card";
import { AttendanceChoice, TurnstileAction, type PublicConfig, type RsvpResponse } from "../shared/contracts";
import { ApiClient, errorMessage } from "./shared/api-client";
import { escapeHtml, requireElement } from "./shared/dom";
import { loadTurnstile, type TurnstileWidgetId } from "./shared/turnstile";

interface RsvpDraft {
  guestGroup: GuestGroup | "";
  name: string;
  phone: string;
  attending: AttendanceChoice | "";
  guests: string;
  dietary: string;
  idempotencyKey: string;
}

enum RsvpStep { Details = 1, Attendance, Party, Review }

class RsvpFormController {
  private readonly flow = requireElement<HTMLElement>("#rsvp-flow");
  private readonly progress = requireElement<HTMLElement>("#progress-fill");
  private readonly stepCount = requireElement<HTMLElement>("#step-count");
  private readonly api = new ApiClient();
  private readonly draft: RsvpDraft = {
    guestGroup: "", name: "", phone: "", attending: "", guests: "1", dietary: "", idempotencyKey: crypto.randomUUID(),
  };
  private step = RsvpStep.Details;
  private turnstileToken = "";
  private turnstileWidget: TurnstileWidgetId | null = null;

  start(): void { this.render(); }

  private saveFields(): void {
    this.flow.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input[name], select[name], textarea[name]")
      .forEach((field) => {
        if (!(field.name in this.draft)) return;
        if (field instanceof HTMLInputElement && field.type === "radio" && !field.checked) return;
        (this.draft as unknown as Record<string, string>)[field.name] = field.value;
      });
  }

  private content(): string {
    if (this.step === RsvpStep.Details) return `<h3 id="flow-title">Your details</h3><label for="guest-name">Full name <span aria-hidden="true">*</span></label><input id="guest-name" name="name" type="text" autocomplete="name" maxlength="100" value="${escapeHtml(this.draft.name)}" placeholder="Your first and last name" required /><label for="guest-phone">Phone number <span class="optional">Optional</span></label><input id="guest-phone" name="phone" type="text" inputmode="tel" autocomplete="tel" maxlength="22" value="${escapeHtml(this.draft.phone)}" placeholder="If we need to contact you" />`;
    if (this.step === RsvpStep.Attendance) return `<h3 id="flow-title">Will you join us?</h3><fieldset class="attendance-options"><legend class="sr-only">Will you attend?</legend><label class="radio-card"><input type="radio" name="attending" value="yes" ${this.draft.attending === AttendanceChoice.Yes ? "checked" : ""} /><span class="radio-dot"></span><span>Joyfully accept</span></label><label class="radio-card"><input type="radio" name="attending" value="no" ${this.draft.attending === AttendanceChoice.No ? "checked" : ""} /><span class="radio-dot"></span><span>Regretfully decline</span></label></fieldset>`;
    if (this.step === RsvpStep.Party) return `<h3 id="flow-title">Your party</h3><p class="flow-description">Please include yourself in the guest count.</p><label for="guest-count">Number of guests</label><select id="guest-count" name="guests">${[1,2,3,4,5,6,7,8].map((count) => `<option value="${count}" ${this.draft.guests === String(count) ? "selected" : ""}>${count} ${count === 1 ? "guest" : "guests"}</option>`).join("")}</select>${renderGuestGroupPicker(this.draft.guestGroup)}<label for="dietary">Dietary needs <span class="optional">Optional</span></label><textarea id="dietary" name="dietary" rows="3" maxlength="400" placeholder="Anything we should know?">${escapeHtml(this.draft.dietary)}</textarea>`;
    return `<h3 id="flow-title">Review your RSVP</h3><p class="flow-description">Your name, guest group, and any phone or dietary details you provide will be saved and visible to the wedding family. Responses are kept until the family requests deletion.</p><dl class="review-list"><div><dt>Name</dt><dd>${escapeHtml(this.draft.name)}</dd></div><div><dt>Phone</dt><dd>${escapeHtml(this.draft.phone || "Not provided")}</dd></div><div><dt>Response</dt><dd>${this.draft.attending === AttendanceChoice.Yes ? "Joyfully accepts" : "Regretfully declines"}</dd></div>${this.draft.attending === AttendanceChoice.Yes ? `<div><dt>Group</dt><dd>${guestGroups[this.draft.guestGroup || GuestGroup.Other].label} ${renderGroupTag(this.draft.guestGroup || GuestGroup.Other)}</dd></div><div><dt>Guests</dt><dd>${escapeHtml(this.draft.guests)} including you</dd></div><div><dt>Dietary needs</dt><dd>${escapeHtml(this.draft.dietary || "None given")}</dd></div>` : ""}</dl><div id="rsvp-challenge"></div><p class="field-hint" id="verification-status">Loading verification…</p>`;
  }

  private render(focus = false): void {
    this.removeChallenge();
    this.stepCount.textContent = `${String(this.step).padStart(2, "0")} — 04`;
    this.progress.style.width = `${this.step * 25}%`;
    const reviewing = this.step === RsvpStep.Review;
    this.flow.innerHTML = `<form id="rsvp-form" novalidate aria-labelledby="flow-title">${this.content()}<p class="form-error" id="form-error" role="alert" hidden></p><div class="form-actions">${this.step > RsvpStep.Details ? `<button class="button-back" type="button" id="rsvp-back">← Back</button>` : `<span></span>`}<button class="button button-dark" type="submit" ${reviewing ? "disabled" : ""}>${reviewing ? "Send RSVP" : "Continue"} <span aria-hidden="true">↗</span></button></div></form>`;
    requireElement<HTMLFormElement>("form", this.flow).addEventListener("submit", (event) => void this.next(event));
    this.flow.querySelector<HTMLButtonElement>("#rsvp-back")?.addEventListener("click", () => this.back());
    bindGuestGroupPicker(this.flow, () => {
      this.saveFields();
      requireElement<HTMLElement>("#form-error", this.flow).hidden = true;
    });
    const heading = this.flow.querySelector<HTMLElement>("h3");
    heading?.setAttribute("tabindex", "-1");
    if (focus) heading?.focus({ preventScroll: true });
    if (reviewing) void this.setupVerification();
  }

  private back(): void {
    this.saveFields();
    this.step = this.step === RsvpStep.Review && this.draft.attending === AttendanceChoice.No
      ? RsvpStep.Attendance
      : this.step - 1;
    this.render(true);
  }

  private showError(message: string, field?: HTMLElement | null): void {
    const element = requireElement<HTMLParagraphElement>("#form-error", this.flow);
    element.textContent = message;
    element.hidden = false;
    field?.focus();
  }

  private async next(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    this.saveFields();
    if (this.step === RsvpStep.Details && this.draft.name.trim().length < 2) return this.showError("Please enter your full name to continue.", this.flow.querySelector("#guest-name"));
    if (this.step === RsvpStep.Details && this.draft.phone.trim() && !/^\+?[0-9 ()-]{7,22}$/.test(this.draft.phone.trim())) return this.showError("Enter a valid phone number or leave it blank.", this.flow.querySelector("#guest-phone"));
    if (this.step === RsvpStep.Attendance && !this.draft.attending) return this.showError("Please choose whether you can attend.", this.flow.querySelector("input[name='attending']"));
    if (this.step === RsvpStep.Party && !this.draft.guestGroup) return this.showError("Please choose your guest group.", this.flow.querySelector("#guest-group"));
    if (this.step === RsvpStep.Review) return void await this.submit();
    this.step = this.step === RsvpStep.Attendance && this.draft.attending === AttendanceChoice.No ? RsvpStep.Review : this.step + 1;
    this.render(true);
  }

  private async setupVerification(): Promise<void> {
    const status = requireElement<HTMLElement>("#verification-status", this.flow);
    try {
      const config = await this.api.request<PublicConfig>("/api/public-config");
      const turnstile = await loadTurnstile();
      if (!status.isConnected) return;
      this.turnstileWidget = turnstile.render("#rsvp-challenge", {
        sitekey: config.turnstileSiteKey,
        action: TurnstileAction.Rsvp,
        callback: (token) => {
          this.turnstileToken = token;
          status.textContent = "Verification complete.";
          requireElement<HTMLButtonElement>("button[type=submit]", this.flow).disabled = false;
        },
        "expired-callback": () => {
          this.turnstileToken = "";
          status.textContent = "Please verify again.";
          requireElement<HTMLButtonElement>("button[type=submit]", this.flow).disabled = true;
        },
      });
      status.textContent = "Complete verification to send your RSVP.";
    } catch (error: unknown) {
      if (status.isConnected) status.textContent = errorMessage(error);
    }
  }

  private async submit(): Promise<void> {
    if (!this.turnstileToken) return this.showError("Complete verification first.");
    const button = requireElement<HTMLButtonElement>("button[type=submit]", this.flow);
    button.disabled = true;
    requireElement<HTMLParagraphElement>("#form-error", this.flow).hidden = true;
    try {
      const result = await this.api.request<RsvpResponse>("/api/rsvp", {
        method: "POST",
        body: JSON.stringify({ ...this.draft, guestGroup: this.draft.attending === AttendanceChoice.Yes ? this.draft.guestGroup : GuestGroup.Other, guests: Number(this.draft.guests), turnstileToken: this.turnstileToken }),
      });
      this.removeChallenge();
      if (this.draft.attending === AttendanceChoice.Yes) {
        const card = { name: this.draft.name.trim(), guests: Number(this.draft.guests), group: this.draft.guestGroup || GuestGroup.Other, reference: result.reference };
        this.flow.innerHTML = renderGuestCard(card) + '<button class="button button-dark" id="save-card" type="button">Save guest card ↓</button><p class="field-hint">Save this card or take a screenshot and show it to an usher. Your table will be confirmed on arrival.</p>';
        requireElement<HTMLButtonElement>("#save-card", this.flow).addEventListener("click", () => {
          try { downloadGuestCard(card); }
          catch { requireElement<HTMLElement>(".field-hint", this.flow).textContent = "Please take a screenshot of your card to keep it."; }
        });
      } else {
        this.flow.innerHTML = `<div class="form-success" role="status"><h3>Thank you, ${escapeHtml(this.draft.name.trim())}.</h3><p>We’ve saved your response. You’ll be with us in spirit.</p></div>`;
      }
      this.stepCount.textContent = "COMPLETE";
      this.progress.style.width = "100%";
      const heading = requireElement<HTMLElement>("h3", this.flow);
      heading.tabIndex = -1;
      heading.focus();
      this.flow.querySelector<HTMLElement>(".guest-card")?.scrollIntoView({ block: "start", behavior: "instant" });
    } catch (error: unknown) {
      this.showError(errorMessage(error));
      this.turnstileToken = "";
      if (this.turnstileWidget != null) window.turnstile?.reset(this.turnstileWidget);
    } finally {
      if (this.flow.contains(button)) button.disabled = !this.turnstileToken;
    }
  }

  private removeChallenge(): void {
    if (this.turnstileWidget != null) window.turnstile?.remove(this.turnstileWidget);
    this.turnstileWidget = null;
    this.turnstileToken = "";
  }
}

new RsvpFormController().start();
